import { supabase } from '@/lib/supabaseClient';
import { ensureAuthenticatedSession } from '@/utils/authentication';
import { getErrorStatus } from '@/utils/errors';

const BUCKET_NAME = 'barrio-hub-manager';
const USER_DIRECTORY = 'users';
const USER_AVATAR_FILE_NAME = 'avatar.png';
const BUCKET_PREFIX = `${BUCKET_NAME}/`;
const DEFAULT_AVATAR_EXPIRATION = 60 * 60 * 24; // 24 horas

const normalizePath = (value: string): string => {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return '';
  }
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  if (trimmed.startsWith(BUCKET_PREFIX)) {
    return trimmed.slice(BUCKET_PREFIX.length);
  }
  return trimmed.replace(/^\/+/, '');
};

export const buildUserAvatarPath = (userId: string) => `${USER_DIRECTORY}/${userId}/${USER_AVATAR_FILE_NAME}`;

export const uploadUserAvatar = async (userId: string, file: File) => {
  await ensureAuthenticatedSession();

  if (!(file instanceof Blob)) {
    throw new Error('El archivo seleccionado no es válido.');
  }

  const objectPath = buildUserAvatarPath(userId);
  const normalizedFile =
    file instanceof File && file.name === USER_AVATAR_FILE_NAME
      ? file
      : new File([file], USER_AVATAR_FILE_NAME, {
          type: file.type || 'application/octet-stream',
        });

  const { error } = await supabase.storage
    .from(BUCKET_NAME)
    .upload(objectPath, normalizedFile, {
      upsert: true,
      contentType: normalizedFile.type || 'application/octet-stream',
      cacheControl: '0',
    });

  if (error) {
    const message = error.message || 'No se pudo subir la foto de perfil.';
    throw new Error(message);
  }

  return objectPath;
};

export const deleteUserAvatar = async (userId: string) => {
  await ensureAuthenticatedSession();

  const objectPath = buildUserAvatarPath(userId);

  const { error } = await supabase.storage.from(BUCKET_NAME).remove([objectPath]);

  if (error) {
    const status = getErrorStatus(error);
    if (status !== 404) {
      const message = error.message || 'No se pudo eliminar la foto de perfil.';
      throw new Error(message);
    }
  }

  return objectPath;
};

export const getUserAvatarSignedUrl = async (
  avatarPath: string,
  expiresInSeconds = DEFAULT_AVATAR_EXPIRATION
): Promise<string | null> => {
  if (!avatarPath) {
    return null;
  }

  if (/^https?:\/\//i.test(avatarPath)) {
    return avatarPath;
  }

  const normalizedPath = normalizePath(avatarPath);
  if (!normalizedPath) {
    return null;
  }

  const candidatePaths = [normalizedPath];
  if (normalizedPath.endsWith(`/${USER_AVATAR_FILE_NAME}`)) {
    const legacyPath = normalizedPath.slice(0, -(USER_AVATAR_FILE_NAME.length + 1));
    if (legacyPath) {
      candidatePaths.push(legacyPath);
    }
  }

  await ensureAuthenticatedSession();

  for (const candidate of candidatePaths) {
    try {
      const { data, error } = await supabase.storage
        .from(BUCKET_NAME)
        .createSignedUrl(candidate, expiresInSeconds);

      if (error) {
        const status = getErrorStatus(error);
        const errorMessage = typeof error === 'object' && error !== null && 'message' in error
          ? String((error as { message?: unknown }).message).toLowerCase()
          : '';
        const isNotFound = status === 404 || status === 400 || errorMessage.includes('not found') || errorMessage.includes('notfound');

        if (isNotFound) {
          continue;
        }
        throw error;
      }

      if (data?.signedUrl) {
        return data.signedUrl;
      }
    } catch (error) {
      console.warn('No se pudo generar la URL firmada del avatar:', error);
      return null;
    }
  }

  return null;
};
